import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe, Plus, Loader2, Trash2, ExternalLink, Search,
  CheckCircle2, AlertCircle, Power
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';


const PUBLISHING_MODE_LABELS = {
  auto_publish:  { label: 'Auto-publish',  colour: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  auto_draft:    { label: 'Auto-draft',    colour: 'bg-blue-50 text-blue-700 border-blue-200' },
  review_queue:  { label: 'Review queue',  colour: 'bg-amber-50 text-amber-700 border-amber-200' },
};


function SubscriptionRow({ sub, onToggle, onDelete, onModeChange }) {
  const mode = PUBLISHING_MODE_LABELS[sub.publishing_mode] || PUBLISHING_MODE_LABELS.auto_draft;
  const [editingMode, setEditingMode] = useState(false);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 shadow-sm">
      <Globe size={14} className="text-slate-400 shrink-0" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/feeds/publications/${sub.publication_id}`}
            className="text-sm text-slate-900 font-medium hover:text-blue-600 transition-colors truncate">
            {sub.publication_name}
          </Link>
          {!editingMode ? (
            <button onClick={() => setEditingMode(true)}
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${mode.colour} hover:opacity-80 transition-opacity`}>
              {mode.label}
            </button>
          ) : (
            <select value={sub.publishing_mode} autoFocus
              onChange={e => { onModeChange(sub, e.target.value); setEditingMode(false); }}
              onBlur={() => setEditingMode(false)}
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white border border-slate-300 text-slate-700">
              <option value="auto_publish">Auto-publish</option>
              <option value="auto_draft">Auto-draft</option>
              <option value="review_queue">Review queue</option>
            </select>
          )}
          {!sub.is_active && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded border border-slate-200">Paused</span>
          )}
        </div>
        {sub.publication_domain && (
          <p className="text-[11px] text-slate-400 mt-0.5">{sub.publication_domain}</p>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => onToggle(sub)}
          className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
          title={sub.is_active ? 'Pause' : 'Resume'}>
          <Power size={13} />
        </button>
        <button onClick={() => onDelete(sub)}
          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
          title="Unsubscribe">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}


function AddSubscriptionPanel({ sourceId, subscribedPubIds, onAdded, onClose }) {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [adding, setAdding]   = useState(null);
  const [mode, setMode]       = useState('auto_draft');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/publications');
        setPublications(data || []);
      } catch (err) {
        toast.error('Failed to load publications');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const available = useMemo(() => {
    let list = publications.filter(p => !subscribedPubIds.includes(p.id));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.domain?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [publications, subscribedPubIds, search]);

  const handleAdd = async (pub) => {
    setAdding(pub.id);
    try {
      await api.post(`/feeds/publications/${pub.id}/subscriptions`, {
        feed_source_id: sourceId,
        publishing_mode: mode,
      });
      toast.success(`${pub.name} subscribed`);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="bg-white border border-blue-200 rounded-xl p-4 mb-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Subscribe a publication</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xs">Cancel</button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search publications..."
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500" />
        </div>
        <select value={mode} onChange={e => setMode(e.target.value)}
          className="px-2 py-1.5 bg-white border border-slate-200 rounded text-xs text-slate-700 focus:outline-none focus:border-blue-500">
          <option value="auto_draft">Auto-draft</option>
          <option value="auto_publish">Auto-publish</option>
          <option value="review_queue">Review queue</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-4 justify-center">
          <Loader2 size={14} className="animate-spin" /> Loading publications...
        </div>
      ) : available.length === 0 ? (
        <p className="text-slate-400 text-sm py-4 text-center">
          {publications.length === subscribedPubIds.length
            ? 'All publications are already subscribed.'
            : 'No publications match your search.'}
        </p>
      ) : (
        <div className="max-h-64 overflow-y-auto space-y-1">
          {available.map(p => (
            <div key={p.id} className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-slate-50 rounded">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900 truncate">{p.name}</p>
                {p.domain && <p className="text-[11px] text-slate-400 truncate">{p.domain}</p>}
              </div>
              <button onClick={() => handleAdd(p)} disabled={adding === p.id}
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-2.5 py-1 rounded text-xs font-medium shrink-0">
                {adding === p.id ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                Subscribe
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function SourceSubscriptionsTab({ sourceId, onChange }) {
  const [subs, setSubs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { load(); }, [sourceId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/feeds/sources/${sourceId}/subscriptions`);
      setSubs(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (sub) => {
    try {
      await api.put(`/feeds/subscriptions/${sub.id}`, { is_active: !sub.is_active });
      toast.success(sub.is_active ? 'Paused' : 'Resumed');
      load(); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`Unsubscribe "${sub.publication_name}" from this source?`)) return;
    try {
      await api.delete(`/feeds/subscriptions/${sub.id}`);
      toast.success('Subscription removed');
      load(); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleModeChange = async (sub, newMode) => {
    try {
      await api.put(`/feeds/subscriptions/${sub.id}`, { publishing_mode: newMode });
      toast.success('Publishing mode updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const subscribedPubIds = subs.map(s => s.publication_id);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-slate-400">
          {subs.length} {subs.length === 1 ? 'publication subscribed' : 'publications subscribed'}
        </p>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors">
            <Plus size={13} /> Add subscription
          </button>
        )}
      </div>

      {showAdd && (
        <AddSubscriptionPanel
          sourceId={sourceId}
          subscribedPubIds={subscribedPubIds}
          onAdded={() => { setShowAdd(false); load(); onChange?.(); }}
          onClose={() => setShowAdd(false)}
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8 gap-2 text-slate-400 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading...
        </div>
      ) : subs.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
          <Globe size={28} className="text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 text-sm font-medium mb-1">No subscriptions yet</p>
          <p className="text-slate-400 text-xs">Subscribe publications to ingest stories from this source.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {subs.map(s => (
            <SubscriptionRow key={s.id} sub={s}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onModeChange={handleModeChange} />
          ))}
        </div>
      )}
    </div>
  );
}